import React, { useEffect, useState } from "react";
import { useStaffAuth } from "../context/StaffAuthContext";
import { getMyTimetable } from "../api/timetable/timetablePortalApi";
import TimetableGrid from "../components/timetable/TimetableGrid";
import { Calendar } from "lucide-react";
import axios from "axios";

import { Card, CardContent, Skeleton } from "../components/ui";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";
const axiosInstance = axios.create({ baseURL: API, withCredentials: true });

export default function MyTimetable() {
  const { staff } = useStaffAuth();
  const [timetableData, setTimetableData] = useState({});
  const [periodsPerDay, setPeriodsPerDay] = useState(null);
  const [loading, setLoading] = useState(true);

  // Determine current day for highlighting (1=Mon ... 6=Sat)
  const todayDayIndex = new Date().getDay(); // 0=Sun, 1=Mon...
  const currentDayOfWeek = todayDayIndex === 0 ? null : todayDayIndex;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch period settings and teacher timetable concurrently
        const [settingsRes, timetableRes] = await Promise.allSettled([
          axiosInstance.get("/timetable-settings-portal"),
          getMyTimetable(),
        ]);

        // 1. Process periodsPerDay directly from DB settings endpoint
        if (settingsRes.status === "fulfilled") {
          const value = settingsRes.value?.data?.periodsPerDay;
          if (value && !isNaN(Number(value))) {
            setPeriodsPerDay(Number(value));
          } else {
            setPeriodsPerDay(null);
          }
        }

        // 2. Process teacher's timetable schedule
        if (timetableRes.status === "fulfilled") {
          const res = timetableRes.value;
          const raw = Array.isArray(res) ? res : res?.data || res?.timetable || [];

          let grouped = {};
          if (Array.isArray(raw)) {
            grouped = raw.reduce((acc, item) => {
              const day = item.dayOfWeek;
              if (!acc[day]) acc[day] = [];

              const periodObj = {
                ...item,
                periodNumber: Number(item.periodNumber || item.periodNo || item.period),
              };

              acc[day].push(periodObj);
              return acc;
            }, {});
          } else {
            grouped = raw || {};
          }
          setTimetableData(grouped);
        }
      } catch (err) {
        console.error("Error fetching timetable or settings:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8 antialiased">
      {/* Page Title Bar */}
      <div className="flex items-center gap-3 border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
            My Timetable — {staff?.name || "Teacher"}
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
            Your personal weekly teaching schedule and period breakdown.
          </p>
        </div>
      </div>

      {/* Main Grid View */}
      {loading ? (
        <Card>
          <CardContent className="p-6 space-y-4">
            <Skeleton height={32} width="100%" />
            <Skeleton height={240} width="100%" />
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <CardContent className="p-2 sm:p-4">
            <TimetableGrid
              timetable={timetableData}
              periodsPerDay={periodsPerDay}
              canManage={false}
              highlightDay={currentDayOfWeek}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}